export interface ColorSwatch {
  hex: string;
  label: string;
  textColor?: 'white' | 'black';
}

export interface InlineLink {
  text: string;
  href: string;
}

export interface BlogSection {
  type: 'paragraph' | 'heading' | 'list' | 'callout' | 'color-demo' | 'contrast-case' | 'code';
  content: string;
  items?: string[];
  links?: InlineLink[];
  fg?: string;
  bg?: string;
  // color-demo fields
  colors?: ColorSwatch[];
  contrastInfo?: { ratio: string; level: 'AAA' | 'AA' | 'FAIL' };
}

export interface BlogPost {
  slug: string;
  title: string;
  date: string;
  readingTime: number;
  excerpt: string;
  tags: string[];
  author: string;
  sections: BlogSection[];
  updated?: string;
  sources?: InlineLink[];
  revision?: string;
}

export const TAG_COLORS: Record<string, string> = {
  Accessibility: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
  WCAG: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
  Design: 'bg-violet-50 text-violet-700 ring-1 ring-violet-200',
  'Color Models': 'bg-sky-50 text-sky-700 ring-1 ring-sky-200',
  Print: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
  'Color Theory': 'bg-rose-50 text-rose-700 ring-1 ring-rose-200',
  UI: 'bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200',
  Branding: 'bg-fuchsia-50 text-fuchsia-700 ring-1 ring-fuchsia-200',
  Psychology: 'bg-orange-50 text-orange-700 ring-1 ring-orange-200',
  'Dark Mode': 'bg-slate-100 text-slate-700 ring-1 ring-slate-300',
  Development: 'bg-cyan-50 text-cyan-700 ring-1 ring-cyan-200',
};

export const blogPosts: BlogPost[] = [
  {
    "slug": "accessible-color-combinations",
    "title": "Build an Accessible Color Palette by Assigning Roles First",
    "date": "2026-03-10",
    "readingTime": 3,
    "excerpt": "Choose surfaces, text, links and state colors as tested pairs, with calculated examples and a handoff checklist.",
    "tags": [
      "Accessibility",
      "WCAG",
      "Design"
    ],
    "author": "Color Match",
    "sections": [
      {
        "type": "paragraph",
        "content": "An accessible palette is a set of usable relationships, not a row of attractive swatches. Start by naming the jobs: page surface, body text, muted text, link, button fill, button label and error message. Then test the pairs that actually touch. A color can succeed in one role and fail in another."
      },
      {
        "type": "heading",
        "content": "Establish the reading surface"
      },
      {
        "type": "contrast-case",
        "content": "A dark body-text candidate on a white page surface.",
        "fg": "1F2937",
        "bg": "FFFFFF"
      },
      {
        "type": "paragraph",
        "content": "Keep this pair as a reference while introducing accent colors. If the default reading experience is stable, decorative choices are easier to evaluate. Check a card surface separately when it differs from the page background; inherited text tokens do not automatically preserve the same ratio."
      },
      {
        "type": "heading",
        "content": "Treat muted text as readable text"
      },
      {
        "type": "contrast-case",
        "content": "A muted-looking candidate that needs a stronger foreground for ordinary body-size text.",
        "fg": "9CA3AF",
        "bg": "FFFFFF"
      },
      {
        "type": "paragraph",
        "content": "A hint can be less prominent without becoming difficult to read. Try smaller spacing changes, reduced emphasis or a different weight before making the color much paler. If the hint contains information needed to complete a form, record it in the contrast inventory just like the label."
      },
      {
        "type": "heading",
        "content": "Assign accents to tested roles"
      },
      {
        "type": "contrast-case",
        "content": "White label on a blue button fill. Use the calculated result for the actual text category.",
        "fg": "FFFFFF",
        "bg": "2563EB"
      },
      {
        "type": "paragraph",
        "content": "A button introduces at least two relationships: label against fill, and the control or its identifying cue against its surroundings. Do not reuse the text result as a check of the button boundary. Test hover and focus arrangements on the surfaces where the component appears."
      },
      {
        "type": "heading",
        "content": "Do not make color carry the message alone"
      },
      {
        "type": "paragraph",
        "content": "An error should have a readable message and an identifying cue, such as an icon or explicit label. A selected item can have a checkmark or a structural change. In a chart, labels and patterns can preserve meaning when colors are hard to distinguish. A color-vision simulation can support review but cannot establish that every person will interpret the design correctly."
      },
      {
        "type": "heading",
        "content": "Prepare a palette handoff"
      },
      {
        "type": "list",
        "content": "",
        "items": [
          "Name each token by role and show its intended surface.",
          "List allowed foreground/background pairs and their checked text category.",
          "Mark decorative accents so they are not accidentally used for small labels.",
          "Record hover, focus, selected and error-state checks.",
          "Include the exact values and a reproducible result link."
        ]
      },
      {
        "type": "paragraph",
        "content": "Build the swatches in Palette Studio, then use the contrast checker for each required pair.",
        "links": [
          {
            "text": "Palette Studio",
            "href": "/studio"
          }
        ]
      },
      {
        "type": "paragraph",
        "content": "The result is a small set of documented decisions. When a surface or text token changes, retest its listed relationships instead of guessing which screens may have been affected."
      }
    ],
    "sources": [
      {
        "text": "W3C: Contrast (Minimum), WCAG 2.2",
        "href": "https://www.w3.org/WAI/WCAG22/Understanding/contrast-minimum.html"
      },
      {
        "text": "W3C: Use of Color",
        "href": "https://www.w3.org/WAI/WCAG22/Understanding/use-of-color.html"
      }
    ],
    "revision": "Replaced unsupported prevalence claims and approximate badges with computed role-specific examples.",
    "updated": "2026-09-13"
  },
  {
    "slug": "cmyk-vs-rgb",
    "title": "RGB to CMYK: Why a Web Conversion Is Only an Approximation",
    "date": "2026-03-12",
    "readingTime": 3,
    "excerpt": "Learn what a profile-free CMYK calculation can show, where it stops, and what to send a printer for a reliable color handoff.",
    "tags": [
      "Color Models",
      "Design",
      "Print"
    ],
    "author": "Color Match",
    "sections": [
      {
        "type": "paragraph",
        "content": "RGB values describe additive color channels used for screens. CMYK values describe ink-channel amounts in a printing workflow. A formula can turn RGB numbers into CMYK numbers, but it cannot predict a specific printed result without the conditions of that workflow. Paper, inks, press behavior and the chosen profile all matter."
      },
      {
        "type": "heading",
        "content": "What the converter actually calculates"
      },
      {
        "type": "paragraph",
        "content": "Normalize the red, green and blue bytes to values from zero to one. Set K to 1 minus the largest normalized RGB value. When K is not one, calculate C=(1-R-K)/(1-K), M=(1-G-K)/(1-K), and Y=(1-B-K)/(1-K). Multiply by 100 to express percentages. For black, handle K=1 separately so there is no division by zero."
      },
      {
        "type": "paragraph",
        "content": "Take #336699: normalized RGB is 0.2, 0.4 and 0.6. K is 0.4. C is approximately 0.6667, M approximately 0.3333, and Y is zero. The simple estimate is therefore about C67 M33 Y0 K40. Rounding can change a converted-back RGB byte, so the displayed percentages are not an exact round-trip storage format."
      },
      {
        "type": "paragraph",
        "content": "Try #336699 in the color converter to reproduce the estimate and inspect the RGB and HEX values together.",
        "links": [
          {
            "text": "color converter",
            "href": "/converter"
          }
        ]
      },
      {
        "type": "heading",
        "content": "What the formula does not know"
      },
      {
        "type": "list",
        "content": "",
        "items": [
          "The intended printing device, ink set and paper.",
          "The source and destination ICC profiles.",
          "The rendering intent and how out-of-gamut colors should be handled.",
          "Production limits such as total ink coverage and black generation."
        ]
      },
      {
        "type": "paragraph",
        "content": "These missing details explain why two managed workflows can produce different CMYK values from a color that started with the same RGB numbers. The simple formula is useful for learning channel relationships or making a preliminary comparison. It should not be treated as a press-ready recipe."
      },
      {
        "type": "heading",
        "content": "A practical handoff to a printer"
      },
      {
        "type": "paragraph",
        "content": "Keep the original RGB artwork and its profile. Ask the print provider for the intended output profile and their conversion requirements before converting the final file. Follow their instructions for proofing, export and black treatment. Review a suitable proof when the color is important to the project, especially for large solid areas or a brand color."
      },
      {
        "type": "paragraph",
        "content": "Do not prescribe a universal rich-black mixture based on a web calculator. Small black text, large black panels and different print processes can have different production needs. The print provider should specify those decisions for the actual job."
      },
      {
        "type": "heading",
        "content": "Separate screen contrast from print evaluation"
      },
      {
        "type": "contrast-case",
        "content": "The same RGB sample can be evaluated for on-screen text contrast. This does not predict ink appearance or print legibility.",
        "fg": "336699",
        "bg": "FFFFFF"
      },
      {
        "type": "paragraph",
        "content": "Use the HEX or RGB values when checking an interface in this tool. Use a managed print workflow when evaluating the physical output. Keeping these tasks separate prevents a precise-looking percentage or contrast ratio from answering a question it was never designed to measure."
      }
    ],
    "sources": [
      {
        "text": "W3C: CSS Color Module Level 4",
        "href": "https://www.w3.org/TR/css-color-4/"
      },
      {
        "text": "International Color Consortium: color-management FAQs",
        "href": "https://www.color.org/faqs/"
      }
    ],
    "revision": "Separated mathematical CMYK estimates from profile-managed print conversion and added reproducible arithmetic.",
    "updated": "2026-09-13"
  },
  {
    "slug": "60-30-10-color-rule",
    "title": "Use the 60-30-10 Color Rule as a Layout Exercise",
    "date": "2026-03-14",
    "readingTime": 2,
    "excerpt": "Try a dominant surface, supporting color and accent without treating a proportion guideline as an accessibility or conversion rule.",
    "tags": [
      "Design",
      "Color Theory",
      "UI"
    ],
    "author": "Color Match",
    "sections": [
      {
        "type": "paragraph",
        "content": "The 60-30-10 rule is a composition exercise: give one color most of the visual area, another a supporting role and a third a small accent role. Use it to examine hierarchy, not to calculate an obligatory percentage for every web page. It does not establish readability or predict which button users will click."
      },
      {
        "type": "heading",
        "content": "Apply it to a reading page"
      },
      {
        "type": "paragraph",
        "content": "For an editorial page, try a cream canvas, navy headings and a coral accent. Count large surfaces and images when assessing visual balance; counting CSS declarations will not describe the visible proportions. Keep the article body comfortable to read even if that pushes the layout away from the nominal ratio."
      },
      {
        "type": "contrast-case",
        "fg": "#172554",
        "bg": "#FFF7ED",
        "content": "Navy reading text on a warm cream surface."
      },
      {
        "type": "contrast-case",
        "fg": "#FF7F50",
        "bg": "#FFF7ED",
        "content": "Coral as small text on the same surface: check the failure before using it."
      },
      {
        "type": "heading",
        "content": "Assign the accent a specific job"
      },
      {
        "type": "paragraph",
        "content": "In this example coral can decorate a category marker. If it becomes the background of a button, add a separately tested on-accent text token. If it marks an error, also supply an error message. A small area allocation does not exempt its content from readability checks."
      },
      {
        "type": "code",
        "content": ":root {\n  --canvas: #FFF7ED;\n  --text: #172554;\n  --accent: #FF7F50;\n  --on-accent: #000000;\n}"
      },
      {
        "type": "heading",
        "content": "Adjust for real content"
      },
      {
        "type": "paragraph",
        "content": "Preview a short article, a long article and a mobile layout. A full-width photograph or stacked card can dominate the screen. Move decorative color out of the way of the content when necessary; do not add filler blocks just to satisfy a ratio. In dark mode, choose role values again instead of assuming the same swatch proportions will read the same way."
      },
      {
        "type": "heading",
        "content": "Check the result"
      },
      {
        "type": "paragraph",
        "content": "Open the cream, navy and coral palette in Studio. Inspect the text pairs and try a UI preview. Keep a screenshot with the content length and viewport noted so your composition decision can be reviewed.",
        "links": [
          {
            "text": "Open the cream, navy and coral palette in Studio",
            "href": "/studio/FFF7ED-172554-FF7F50-000000"
          }
        ]
      }
    ],
    "sources": [
      {
        "text": "W3C: Contrast (Minimum)",
        "href": "https://www.w3.org/WAI/WCAG22/Understanding/contrast-minimum.html"
      },
      {
        "text": "W3C: Use of Color",
        "href": "https://www.w3.org/WAI/WCAG22/Understanding/use-of-color.html"
      }
    ],
    "updated": "2026-09-13",
    "revision": "Rebuilt around explicit design choices, reproducible examples and documented limitations; retained the existing URL."
  },
  {
    "slug": "color-psychology",
    "title": "Color Psychology: Test Associations in Context",
    "date": "2026-03-16",
    "readingTime": 2,
    "excerpt": "Separate color-emotion research from brand claims and use a concrete research exercise before choosing a palette.",
    "tags": [
      "Color Theory",
      "Branding",
      "Psychology"
    ],
    "author": "Color Match",
    "sections": [
      {
        "type": "paragraph",
        "content": "Treat a color association as a hypothesis about a particular audience and context. A color word, a swatch and a purchase screen are different stimuli. Research on emotion associations cannot tell you that changing one button to blue will make people trust your product or buy more."
      },
      {
        "type": "heading",
        "content": "What the research can support"
      },
      {
        "type": "paragraph",
        "content": "Jonauskaite and colleagues studied color-emotion associations across 30 nations and found shared patterns as well as relationships with linguistic and geographic proximity. That is evidence about associations in the study setting, not a universal branding dictionary or a causal test of commercial outcomes."
      },
      {
        "type": "heading",
        "content": "Translate a vague claim into a task"
      },
      {
        "type": "paragraph",
        "content": "Suppose a team says its scheduling product should look \"calm.\" Prepare two otherwise identical booking screens: one with navy on cream and another with dark green on pale green. Ask intended users to find the next appointment, explain the primary action and describe their impressions in their own words. Keep typography, wording and layout fixed for this comparison."
      },
      {
        "type": "contrast-case",
        "fg": "#172554",
        "bg": "#FFF7ED",
        "content": "Booking concept A: navy and cream."
      },
      {
        "type": "contrast-case",
        "fg": "#14532D",
        "bg": "#F0FDF4",
        "content": "Booking concept B: green and pale green."
      },
      {
        "type": "heading",
        "content": "Record the limits of your sample"
      },
      {
        "type": "paragraph",
        "content": "Note who participated, the language, screen, task and context. A few interviews can reveal confusion but should not be reported as a percentage of the whole market. Separate preference from task success: a participant can like a color and still miss the action. Avoid inventing audience findings when only your team reviewed the mockups."
      },
      {
        "type": "heading",
        "content": "Preserve meaning and readability"
      },
      {
        "type": "paragraph",
        "content": "If green also marks confirmed bookings, write \"Confirmed\" beside it. If a red action cancels an appointment, use an explicit label and a clear confirmation flow. Colors support those messages; they should not have to communicate the entire interaction."
      },
      {
        "type": "paragraph",
        "content": "Compare the two candidate text colors in the tool, then return to your complete screens for the audience exercise. Numeric contrast and emotional interpretation answer different questions.",
        "links": [
          {
            "text": "Compare the two candidate text colors",
            "href": "/compare/172554-14532D"
          }
        ]
      }
    ],
    "sources": [
      {
        "text": "Jonauskaite et al. (2020): Color-emotion associations across nations",
        "href": "https://www.psychologicalscience.org/journals/psychological-science/0956797620948810/"
      },
      {
        "text": "W3C: Use of Color",
        "href": "https://www.w3.org/WAI/WCAG22/Understanding/use-of-color.html"
      }
    ],
    "updated": "2026-09-13",
    "revision": "Rebuilt around explicit design choices, reproducible examples and documented limitations; retained the existing URL."
  },
  {
    "slug": "dark-mode-palette",
    "title": "Build a Dark Mode Palette with Tested Color Roles",
    "date": "2026-03-18",
    "readingTime": 2,
    "excerpt": "Work through a dark canvas, surface, body text and action color, then check their actual pairings before exporting.",
    "tags": [
      "Dark Mode",
      "UI",
      "Design"
    ],
    "author": "Color Match",
    "sections": [
      {
        "type": "paragraph",
        "content": "Build dark mode by assigning roles rather than inverting every light-mode value. The example here is a reading interface: a dark canvas contains raised cards, body text and a single primary action. These are editable design choices, not a claim that one dark palette suits every reader."
      },
      {
        "type": "heading",
        "content": "Choose a canvas and surface"
      },
      {
        "type": "paragraph",
        "content": "Start with #111827 for the canvas and #1F2937 for cards. Their small lightness difference suggests grouping; it is insufficient evidence that a field or essential boundary is identifiable. Where that boundary matters, add a border and measure it against the adjacent surface. Pure black is also a valid design choice; there is no universal rule forbidding it."
      },
      {
        "type": "heading",
        "content": "Measure text on each surface"
      },
      {
        "type": "contrast-case",
        "fg": "#F9FAFB",
        "bg": "#111827",
        "content": "Body text on the page canvas."
      },
      {
        "type": "contrast-case",
        "fg": "#9CA3AF",
        "bg": "#1F2937",
        "content": "Secondary text on a card, measured independently."
      },
      {
        "type": "paragraph",
        "content": "Try longer paragraphs at the actual font size. Dim secondary text can disappear when its card changes color. Our example keeps opacity at 1 so the input pair matches the rendered colors. An opacity setting would require recalculating the composite color."
      },
      {
        "type": "heading",
        "content": "Use a separate action token"
      },
      {
        "type": "contrast-case",
        "fg": "#111827",
        "bg": "#93C5FD",
        "content": "Dark text on a pale blue action button."
      },
      {
        "type": "code",
        "content": ":root[data-theme=\"dark\"] {\n  --surface-canvas: #111827;\n  --surface-card: #1F2937;\n  --text-primary: #F9FAFB;\n  --text-secondary: #9CA3AF;\n  --action: #93C5FD;\n  --on-action: #111827;\n}"
      },
      {
        "type": "heading",
        "content": "Validate the transition"
      },
      {
        "type": "paragraph",
        "content": "In your app, test switching themes while a menu, error message or dialog is open. Retest hover and focus colors; an inherited light-theme token is easy to miss. Offer a usable theme choice rather than assuming everyone prefers the same brightness."
      },
      {
        "type": "paragraph",
        "content": "Open this dark palette in Studio to inspect the pair matrix and export its values. Assign the role aliases above in your stylesheet; an automatically generated scale does not carry those decisions for you.",
        "links": [
          {
            "text": "Open this dark palette in Studio",
            "href": "/studio/111827-1F2937-F9FAFB-9CA3AF-93C5FD"
          }
        ]
      }
    ],
    "sources": [
      {
        "text": "W3C: Contrast (Minimum)",
        "href": "https://www.w3.org/WAI/WCAG22/Understanding/contrast-minimum.html"
      },
      {
        "text": "W3C: Non-text Contrast",
        "href": "https://www.w3.org/WAI/WCAG22/Understanding/non-text-contrast.html"
      },
      {
        "text": "MDN: prefers-color-scheme",
        "href": "https://developer.mozilla.org/en-US/docs/Web/CSS/@media/prefers-color-scheme"
      }
    ],
    "updated": "2026-09-13",
    "revision": "Rechecked technical claims, replaced unsupported generalizations, and added reproducible examples and source links."
  },
  {
    "slug": "wcag-contrast-ratio-guide",
    "title": "WCAG Contrast Ratios: Calculate, Interpret and Fix a Failing Pair",
    "date": "2026-03-20",
    "readingTime": 3,
    "excerpt": "A worked explanation of luminance, unrounded thresholds and foreground fixes, including the near-miss of #777777 on white.",
    "tags": [
      "Accessibility",
      "WCAG",
      "Development"
    ],
    "author": "Color Match",
    "sections": [
      {
        "type": "paragraph",
        "content": "A contrast ratio answers a narrow question: how far apart are the relative luminances of two colors? It does not tell you whether a palette is attractive, a button is obvious, or an entire page is accessible. Use it for a specified foreground, background and text role, then test the result in the interface."
      },
      {
        "type": "heading",
        "content": "Start with a pair that almost passes"
      },
      {
        "type": "contrast-case",
        "content": "Medium gray body text on a white surface. The displayed ratio is calculated from these exact opaque sRGB values.",
        "fg": "777777",
        "bg": "FFFFFF"
      },
      {
        "type": "paragraph",
        "content": "This pair is close enough to expose a common implementation error. A rounded display can look like 4.5:1, but the actual value is below that threshold. A calculator must compare the original number with the requirement before formatting the result. Changing the foreground by only one channel step can change the decision near a boundary."
      },
      {
        "type": "heading",
        "content": "Calculate from channels, not an RGB average"
      },
      {
        "type": "paragraph",
        "content": "Split the HEX code into red, green and blue bytes. Divide each byte by 255. For each normalized channel c, use c/12.92 when c is at most 0.04045; otherwise use ((c + 0.055)/1.055)^2.4. Combine the linear values using 0.2126 red, 0.7152 green and 0.0722 blue. Finally divide the lighter luminance plus 0.05 by the darker luminance plus 0.05."
      },
      {
        "type": "paragraph",
        "content": "For black the luminance is zero, and for white it is one. That gives 1.05/0.05 = 21. Identical colors always give 1. These are useful baseline checks when comparing calculators or investigating a regression. Averaging the three original byte values misses both the transfer function and the different channel weights."
      },
      {
        "type": "heading",
        "content": "Choose the requirement for the actual text"
      },
      {
        "type": "list",
        "content": "",
        "items": [
          "AA normal text: at least 4.5:1. AAA normal text: at least 7:1.",
          "AA large text: at least 3:1. AAA large text: at least 4.5:1.",
          "Large text begins at 18pt (24 CSS px), or 14pt (about 18.67 CSS px) when bold. A 14px bold label is still normal text."
        ]
      },
      {
        "type": "heading",
        "content": "Fix the foreground, then verify the exported value"
      },
      {
        "type": "paragraph",
        "content": "Open this example in the contrast checker, choose the target, and apply the suggested foreground. Keep the surface fixed so the effect of the change is clear.",
        "links": [
          {
            "text": "contrast checker",
            "href": "/contrast-checker?fg=777777&bg=FFFFFF"
          }
        ]
      },
      {
        "type": "paragraph",
        "content": "Color Match searches lighter and darker OKLCH values and tests the final rounded sRGB output. Gamut clipping can change saturation or hue, so inspect the suggested color beside the original. The tool does not claim to find a globally optimal color. It offers a passing candidate when one is available."
      },
      {
        "type": "contrast-case",
        "content": "A one-step darker neutral demonstrates why the exact exported RGB bytes matter.",
        "fg": "767676",
        "bg": "FFFFFF"
      },
      {
        "type": "heading",
        "content": "Know when the input is incomplete"
      },
      {
        "type": "paragraph",
        "content": "An opaque HEX pair does not describe translucent text, a photograph, a gradient or a blend mode. Work out the composited colors and inspect the least favorable region behind the text. Keep a record of the element, state, font size, weight, pair and threshold. This makes the test repeatable after a design-token update."
      }
    ],
    "sources": [
      {
        "text": "W3C: Contrast (Minimum), WCAG 2.2",
        "href": "https://www.w3.org/WAI/WCAG22/Understanding/contrast-minimum.html"
      }
    ],
    "revision": "Replaced approximate examples with calculated pairs; corrected large-text units and added reproducible fixes.",
    "updated": "2026-09-13"
  },
  {
    "slug": "hsl-color-model",
    "title": "HSL Color Model: A Practical Guide for CSS",
    "date": "2026-03-22",
    "readingTime": 2,
    "excerpt": "Read hue, saturation and lightness, try a controlled color change, and understand why equal HSL lightness does not mean equal contrast.",
    "tags": [
      "Color Models",
      "Development",
      "Design"
    ],
    "author": "Color Match",
    "sections": [
      {
        "type": "paragraph",
        "content": "HSL reorganizes sRGB into hue angle, saturation and lightness. It can make an edit such as rotating a hue easier to express than changing three RGB channels. HEX and HSL can describe the same sRGB color; neither notation makes a design more accessible by itself."
      },
      {
        "type": "heading",
        "content": "Read one concrete value"
      },
      {
        "type": "code",
        "content": ".sample { background: hsl(0 100% 50%); }\n/* Equivalent opaque sRGB color: #FF0000 */\n.sample-soft { background: hsl(0 50% 50%); }\n.sample-light { background: hsl(0 100% 75%); }"
      },
      {
        "type": "paragraph",
        "content": "The first sample is red. The second reduces saturation while holding the numeric hue and lightness. The third changes lightness. Compare each next to the unchanged sample to learn what one dimension does before building a multi-step palette."
      },
      {
        "type": "heading",
        "content": "Do not confuse HSL L with luminance"
      },
      {
        "type": "contrast-case",
        "fg": "#FFFF00",
        "bg": "#FFFFFF",
        "content": "Yellow: hsl(60 100% 50%) on white."
      },
      {
        "type": "contrast-case",
        "fg": "#0000FF",
        "bg": "#FFFFFF",
        "content": "Blue: hsl(240 100% 50%) on white."
      },
      {
        "type": "paragraph",
        "content": "The shared 50% lightness value describes the RGB construction, not equal perceptual brightness. The calculated results below each example show the consequence for white backgrounds. You cannot infer a passing pair from a fixed difference in HSL lightness."
      },
      {
        "type": "heading",
        "content": "Handle hue and neutral colors deliberately"
      },
      {
        "type": "paragraph",
        "content": "Hue wraps around a circle: 360 degrees returns to zero. At zero saturation the result is gray and hue no longer changes its appearance. A color tool may retain a hue for later editing or report zero; record the actual HEX when exact equality matters."
      },
      {
        "type": "heading",
        "content": "Convert a value and round consciously"
      },
      {
        "type": "paragraph",
        "content": "Use the converter to inspect the HSL for a known HEX, then copy the representation your stylesheet needs. Color Match rounds displayed HSL components, so converting that rounded text back may differ by a small RGB amount. Preserve the original HEX when byte-for-byte fidelity is the priority.",
        "links": [
          {
            "text": "Use the converter",
            "href": "/converter"
          }
        ]
      }
    ],
    "sources": [
      {
        "text": "MDN: hsl()",
        "href": "https://developer.mozilla.org/en-US/docs/Web/CSS/color_value/hsl"
      },
      {
        "text": "W3C: CSS Color 4",
        "href": "https://www.w3.org/TR/css-color-4/"
      }
    ],
    "updated": "2026-09-13",
    "revision": "Rechecked technical claims, replaced unsupported generalizations, and added reproducible examples and source links."
  },
  {
    "slug": "brand-color-palette",
    "title": "Build a Brand Palette from Real Interface Roles",
    "date": "2026-03-23",
    "readingTime": 2,
    "excerpt": "Turn a brand accent into surface, text and action tokens, with separate decisions for digital use and print handoff.",
    "tags": [
      "Branding",
      "Design",
      "Color Theory"
    ],
    "author": "Color Match",
    "sections": [
      {
        "type": "paragraph",
        "content": "Begin a brand palette with the work it must support. A small software product might need a landing page, navigation, buttons, forms, charts and email. Make a list of those contexts before choosing supporting swatches. The exercise below preserves a coral accent while assigning darker values to reading roles."
      },
      {
        "type": "heading",
        "content": "Separate brand recognition from text roles"
      },
      {
        "type": "contrast-case",
        "fg": "#FF7F50",
        "bg": "#FFFFFF",
        "content": "The coral accent tested as small text on white."
      },
      {
        "type": "contrast-case",
        "fg": "#9A3412",
        "bg": "#FFFFFF",
        "content": "A darker orange candidate for text on white."
      },
      {
        "type": "paragraph",
        "content": "The first color can remain in illustrations and decorative surfaces even if it fails as small text. The second is a role-specific candidate, not a replacement brand identity. Put the chosen values next to real headings, field labels and button copy to review their behavior."
      },
      {
        "type": "heading",
        "content": "Keep the palette small enough to inspect"
      },
      {
        "type": "code",
        "content": ":root {\n  --brand-accent: #FF7F50;\n  --brand-ink: #9A3412;\n  --surface: #FFFFFF;\n  --text: #111827;\n  --on-accent: #000000;\n}"
      },
      {
        "type": "paragraph",
        "content": "Add semantic success, warning or error values only when the UI needs them. Name their purpose and keep messages understandable beyond color. A harmony can suggest supporting hues, but it cannot decide your product's status vocabulary or validate every combination."
      },
      {
        "type": "heading",
        "content": "Test before handing off"
      },
      {
        "type": "paragraph",
        "content": "Inspect at least a navigation bar, one form and an action button in normal, hover and focused states. Write down which text belongs on each surface. Keep those pairings with the exported tokens so another designer does not use a decorative accent as a small text color."
      },
      {
        "type": "heading",
        "content": "Make print a separate conversion task"
      },
      {
        "type": "paragraph",
        "content": "For print, ask the printer for its target profile and proof requirements. The CMYK numbers in this tool are unprofiled estimates. Do not promise an exact ink match from a HEX conversion or use a screen preview as a press proof."
      },
      {
        "type": "paragraph",
        "content": "Open the brand example in Studio and export its values. The role aliases above are design decisions you can adapt to your own product.",
        "links": [
          {
            "text": "Open the brand example in Studio",
            "href": "/studio/FF7F50-9A3412-FFFFFF-111827-000000"
          }
        ]
      }
    ],
    "sources": [
      {
        "text": "W3C: Contrast (Minimum)",
        "href": "https://www.w3.org/WAI/WCAG22/Understanding/contrast-minimum.html"
      },
      {
        "text": "W3C: Use of Color",
        "href": "https://www.w3.org/WAI/WCAG22/Understanding/use-of-color.html"
      },
      {
        "text": "W3C: CSS Color 4, device and color-space distinctions",
        "href": "https://www.w3.org/TR/css-color-4/"
      }
    ],
    "updated": "2026-09-13",
    "revision": "Rebuilt around explicit design choices, reproducible examples and documented limitations; retained the existing URL."
  },
  {
    "slug": "best-color-combinations-2026",
    "title": "Four Website Color Pairs to Test in 2026",
    "date": "2026-03-24",
    "readingTime": 2,
    "excerpt": "Compare four concrete text and background pairs with calculated results and role-specific limitations; these are examples, not a trend ranking.",
    "tags": [
      "Design",
      "Color Theory",
      "UI"
    ],
    "author": "Color Match",
    "sections": [
      {
        "type": "paragraph",
        "content": "These four pairs are editorial examples for different reading surfaces. They are not a survey of 2026 websites, a popularity ranking or evidence of improved conversion. Each example names the intended foreground and background so its calculation can be reproduced."
      },
      {
        "type": "heading",
        "content": "1. Navy text on warm cream"
      },
      {
        "type": "contrast-case",
        "fg": "#172554",
        "bg": "#FFF7ED",
        "content": "A candidate for a reading page with a warm surface."
      },
      {
        "type": "paragraph",
        "content": "Use navy for the body and cream for the canvas in a small article mockup. Check links separately if you introduce an accent. A cream background can alter the contrast of text that was previously checked on pure white."
      },
      {
        "type": "heading",
        "content": "2. Dark green text on a pale green surface"
      },
      {
        "type": "contrast-case",
        "fg": "#14532D",
        "bg": "#F0FDF4",
        "content": "A candidate for a checklist or calm visual theme."
      },
      {
        "type": "paragraph",
        "content": "Use the palette as a visual direction, not a claim that green communicates health or sustainability to every audience. If green also signals success, add explicit status text so decorative and functional uses do not compete."
      },
      {
        "type": "heading",
        "content": "3. Burnt orange text on a warm neutral"
      },
      {
        "type": "contrast-case",
        "fg": "#9A3412",
        "bg": "#FFF7ED",
        "content": "A warm accent used in a readable text role."
      },
      {
        "type": "paragraph",
        "content": "This darker orange is distinct from bright terracotta used as decoration. Test a product card with its price, caption and focus ring; passing the headline pair does not validate the remaining elements."
      },
      {
        "type": "heading",
        "content": "4. Light violet text on a dark surface"
      },
      {
        "type": "contrast-case",
        "fg": "#C4B5FD",
        "bg": "#18181B",
        "content": "A candidate for a dark navigation or callout."
      },
      {
        "type": "paragraph",
        "content": "Inspect this pair at your actual text size and with long content. For a filled violet button, its text partner must change and be measured again. A dark page does not make every saturated accent suitable for text."
      },
      {
        "type": "heading",
        "content": "Pick by the component you need"
      },
      {
        "type": "paragraph",
        "content": "Open all four light-theme roles in Studio to compare their cross-pairings. Choose a pair for an actual screen, record the measured result and review the rendered component. Keep the recommendation specific to that use.",
        "links": [
          {
            "text": "Open all four light-theme roles in Studio",
            "href": "/studio/172554-FFF7ED-14532D-F0FDF4-9A3412"
          }
        ]
      }
    ],
    "sources": [
      {
        "text": "W3C: Contrast (Minimum)",
        "href": "https://www.w3.org/WAI/WCAG22/Understanding/contrast-minimum.html"
      }
    ],
    "updated": "2026-09-13",
    "revision": "Rebuilt around explicit design choices, reproducible examples and documented limitations; retained the existing URL."
  },
  {
    "slug": "color-blindness-design",
    "title": "Design Color Cues That Remain Understandable",
    "date": "2026-03-25",
    "readingTime": 2,
    "excerpt": "Use explicit labels, patterns and shape alongside color, with a status-message example and clear limits for simulations.",
    "tags": [
      "Accessibility",
      "Design",
      "WCAG"
    ],
    "author": "Color Match",
    "sections": [
      {
        "type": "paragraph",
        "content": "Design a status so its meaning survives when two hues are difficult to distinguish. A simulation can help reveal a fragile color choice, but it cannot reproduce every person's vision or substitute for feedback from users. Avoid claiming that a single palette is universally color-blind safe."
      },
      {
        "type": "heading",
        "content": "Start with meaning"
      },
      {
        "type": "paragraph",
        "content": "Imagine an order list with green and red dots. Without labels, a reader must remember which dot means paid and distinguish it from overdue. Change the rows to a check icon plus \"Paid\" and an alert icon plus \"Overdue\". The colors can remain as supporting cues; the words now carry the distinction."
      },
      {
        "type": "code",
        "content": "Paid      [check icon]   Invoice 104\nOverdue   [alert icon]   Invoice 105\nPending   [clock icon]   Invoice 106"
      },
      {
        "type": "heading",
        "content": "Check readability separately"
      },
      {
        "type": "contrast-case",
        "fg": "#166534",
        "bg": "#FFFFFF",
        "content": "Paid label on white."
      },
      {
        "type": "contrast-case",
        "fg": "#B91C1C",
        "bg": "#FFFFFF",
        "content": "Overdue label on white."
      },
      {
        "type": "paragraph",
        "content": "These checks measure each label against white. They do not prove that green and red are distinguishable from one another. Also test the icons against their surfaces and inspect the actual font size at zoom. Do not show the result as a certificate for the entire interface."
      },
      {
        "type": "heading",
        "content": "Understand simulation categories"
      },
      {
        "type": "paragraph",
        "content": "Protan, deutan and tritan simulations model different changes to cone responses. Real differences vary in degree and context. Our simulator transforms sampled palette colors, not a user's display or their complete visual experience. Use several settings to look for lost distinctions, and include grayscale as another diagnostic view."
      },
      {
        "type": "heading",
        "content": "Review charts and controls"
      },
      {
        "type": "paragraph",
        "content": "Give chart series direct names and distinct line styles or markers. Label swatches with their values and show selected controls with a check or border. Test whether a colleague can complete the task from a monochrome printout; use the result to locate reliance on color, not to diagnose vision."
      },
      {
        "type": "paragraph",
        "content": "Explore the red sample and open its accessibility section to compare simulated swatches. Keep labels visible in the final UI even when the simulation appears clear.",
        "links": [
          {
            "text": "Explore the red sample",
            "href": "/color/FF0000"
          }
        ]
      }
    ],
    "sources": [
      {
        "text": "W3C: Use of Color",
        "href": "https://www.w3.org/WAI/WCAG22/Understanding/use-of-color.html"
      },
      {
        "text": "W3C: Non-text Contrast",
        "href": "https://www.w3.org/WAI/WCAG22/Understanding/non-text-contrast.html"
      },
      {
        "text": "W3C: Complex Images",
        "href": "https://www.w3.org/WAI/tutorials/images/complex/"
      }
    ],
    "updated": "2026-09-13",
    "revision": "Rechecked technical claims, replaced unsupported generalizations, and added reproducible examples and source links."
  },
  {
    "slug": "css-color-functions",
    "title": "CSS Color Functions: Values, Alpha and Fallbacks",
    "date": "2026-03-26",
    "readingTime": 2,
    "excerpt": "Choose a color notation based on the operation you need, with runnable CSS examples and explicit conversion limits.",
    "tags": [
      "Development",
      "Color Models",
      "UI"
    ],
    "author": "Color Match",
    "sections": [
      {
        "type": "paragraph",
        "content": "Use rgb() for channel values, hsl() for hue-oriented edits and oklch() for perceptual palette exploration. Choosing a notation is separate from choosing a readable foreground/background pair. Start with an opaque example, then add transparency only when you know what will be behind it."
      },
      {
        "type": "heading",
        "content": "Write equivalent opaque sRGB values"
      },
      {
        "type": "code",
        "content": ".hex { color: #FF0000; }\n.rgb { color: rgb(255 0 0); }\n.hsl { color: hsl(0 100% 50%); }"
      },
      {
        "type": "paragraph",
        "content": "These three declarations describe the same red. In modern space-separated syntax, an optional alpha follows a slash. A transparent color describes a compositing input; its appearance depends on the background, so passing the original RGB triplet to an opaque checker is insufficient."
      },
      {
        "type": "heading",
        "content": "Treat alpha as part of the design"
      },
      {
        "type": "code",
        "content": ".overlay { background: rgb(0 0 0 / 50%); }"
      },
      {
        "type": "paragraph",
        "content": "Place this overlay over white and then over a photograph. The results differ even though the declaration does not. For a contrast test, flatten or calculate the relevant foreground/background composition in its actual context. Keep essential text on a controlled surface when that composition varies."
      },
      {
        "type": "heading",
        "content": "Declare a deliberate fallback"
      },
      {
        "type": "code",
        "content": ".badge {\n  background: #6366F1;\n  background: oklch(60% 0.20 280);\n  color: #FFFFFF;\n}"
      },
      {
        "type": "paragraph",
        "content": "The two backgrounds are intentionally separate candidates, not equivalent conversions. Inspect both in the browsers you support and test each with white text. Syntax support alone does not promise identical gamut mapping across displays."
      },
      {
        "type": "heading",
        "content": "Mix only after choosing a color space"
      },
      {
        "type": "code",
        "content": ".tint { background: #FF8080; background: color-mix(in srgb, red 50%, white); }"
      },
      {
        "type": "paragraph",
        "content": "Open the converter to inspect a chosen opaque value. Use the color-mix guide for weighting and interpolation examples, then check the finished pair rather than assuming that lightening a color improves every role.",
        "links": [
          {
            "text": "color-mix guide",
            "href": "/blog/css-color-mix-guide"
          }
        ]
      }
    ],
    "sources": [
      {
        "text": "W3C: CSS Color 4",
        "href": "https://www.w3.org/TR/css-color-4/"
      },
      {
        "text": "W3C: CSS Color 5",
        "href": "https://www.w3.org/TR/css-color-5/"
      }
    ],
    "updated": "2026-09-13",
    "revision": "Rechecked technical claims, replaced unsupported generalizations, and added reproducible examples and source links."
  },
  {
    "slug": "data-visualization-colors",
    "title": "Choose Chart Colors by the Question and the Data",
    "date": "2026-03-27",
    "readingTime": 2,
    "excerpt": "Distinguish categorical, ordered and diverging palettes, and keep chart meaning available through labels and data.",
    "tags": [
      "Design",
      "Accessibility",
      "Color Theory"
    ],
    "author": "Color Match",
    "sections": [
      {
        "type": "paragraph",
        "content": "Start with what the reader must compare. Category names, ordered measurements and deviations around a meaningful midpoint need different encodings. The examples here describe a small chart design exercise; no palette can replace a truthful scale, clear labels or access to the underlying values."
      },
      {
        "type": "heading",
        "content": "Categories: preserve identity"
      },
      {
        "type": "paragraph",
        "content": "For three product lines, assign a stable color to each line across every chart. Add direct labels and distinct markers such as circles, squares and triangles. Do not assign lighter shades in a way that suggests one category is inherently larger or better unless the data actually has that order."
      },
      {
        "type": "code",
        "content": "Product A: #1D4ED8, solid line, circle marker\nProduct B: #B45309, dashed line, square marker\nProduct C: #7E22CE, dotted line, triangle marker"
      },
      {
        "type": "heading",
        "content": "Ordered values: show the sequence"
      },
      {
        "type": "paragraph",
        "content": "For a heatmap of counts, use a clearly ordered lightness progression and label the numeric range. Include a separate treatment for missing data; zero is a value, while missing is unknown. Inspect whether the legend remains understandable when printed without color."
      },
      {
        "type": "heading",
        "content": "Diverging values: define the center"
      },
      {
        "type": "paragraph",
        "content": "A temperature deviation chart can place zero at a neutral midpoint with two directions on either side. A chart of strictly positive revenue has no automatic reason to diverge around the middle of its range. State the baseline in the title or legend so color does not create an unsupported story."
      },
      {
        "type": "heading",
        "content": "Check essential marks and reading text"
      },
      {
        "type": "contrast-case",
        "fg": "#1D4ED8",
        "bg": "#FFFFFF",
        "content": "Blue series mark against a white chart surface; also inspect its actual line width."
      },
      {
        "type": "contrast-case",
        "fg": "#B45309",
        "bg": "#FFFFFF",
        "content": "Amber series mark against the same surface."
      },
      {
        "type": "paragraph",
        "content": "These are background checks, not proof that the two series are distinguishable from one another. Inspect overlaps, line widths, labels and markers. W3C guidance on complex images also calls for conveying the important data and relationships through appropriate text alternatives or accompanying content."
      },
      {
        "type": "heading",
        "content": "Publish the values as well"
      },
      {
        "type": "paragraph",
        "content": "Open the categorical example in Studio and inspect its pair matrix. In the final chart, include a caption describing the main comparison and a readable table or equivalent access to the values. Test navigation and tooltip access beyond a mouse pointer.",
        "links": [
          {
            "text": "Open the categorical example in Studio",
            "href": "/studio/1D4ED8-B45309-7E22CE-FFFFFF"
          }
        ]
      }
    ],
    "sources": [
      {
        "text": "W3C: Complex Images",
        "href": "https://www.w3.org/WAI/tutorials/images/complex/"
      },
      {
        "text": "W3C: Non-text Contrast",
        "href": "https://www.w3.org/WAI/WCAG22/Understanding/non-text-contrast.html"
      },
      {
        "text": "W3C: Use of Color",
        "href": "https://www.w3.org/WAI/WCAG22/Understanding/use-of-color.html"
      }
    ],
    "updated": "2026-09-13",
    "revision": "Rebuilt around explicit design choices, reproducible examples and documented limitations; retained the existing URL."
  },
  {
    "slug": "color-temperature",
    "title": "Warm and Cool Colors: Compare the Context",
    "date": "2026-03-28",
    "readingTime": 2,
    "excerpt": "Use warm and cool as relative design descriptions, then inspect the actual reading pair and composition.",
    "tags": [
      "Color Theory",
      "Design",
      "UI"
    ],
    "author": "Color Match",
    "sections": [
      {
        "type": "paragraph",
        "content": "In interface design, \"warm\" and \"cool\" usually describe a visual relationship: orange may feel warmer than a neighboring blue. This usage is not a Kelvin measurement of a display white point. It also does not provide a contrast value or a guaranteed emotional effect."
      },
      {
        "type": "heading",
        "content": "Compare one element on two surfaces"
      },
      {
        "type": "paragraph",
        "content": "Take navy text #172554 and place it first on cream #FFF7ED, then on pale blue #EFF6FF. Keep the same size, weight and content. This controlled change makes the background choice easier to discuss than comparing two completely different landing pages."
      },
      {
        "type": "contrast-case",
        "fg": "#172554",
        "bg": "#FFF7ED",
        "content": "Navy on the warm-neutral candidate."
      },
      {
        "type": "contrast-case",
        "fg": "#172554",
        "bg": "#EFF6FF",
        "content": "The same navy on the cool-neutral candidate."
      },
      {
        "type": "heading",
        "content": "Use temperature as a composition choice"
      },
      {
        "type": "paragraph",
        "content": "For a product card, a warm accent might connect with a photograph that contains similar hues. It might also compete with the photograph. Inspect the real asset, not only the swatches. Describe the visible result in your review: for example, \"the price badge attracts attention before the product name,\" rather than claiming that warm colors always advance."
      },
      {
        "type": "heading",
        "content": "Keep the numeric model separate"
      },
      {
        "type": "paragraph",
        "content": "CSS hue is an angle in a specified color model. Rotating it changes a model coordinate; it does not predict every perceived warm/cool relationship. At very low saturation the hue label can become less useful, so retain the actual value and view the surrounding colors."
      },
      {
        "type": "code",
        "content": ":root {\n  --surface-warm: #FFF7ED;\n  --surface-cool: #EFF6FF;\n  --reading-ink: #172554;\n}"
      },
      {
        "type": "heading",
        "content": "Choose with a concrete screen"
      },
      {
        "type": "paragraph",
        "content": "Compare the two surfaces side by side, then try the winning candidate in an article or product card. Check its text pair and gather audience feedback if the choice is meant to communicate a particular mood.",
        "links": [
          {
            "text": "Compare the two surfaces side by side",
            "href": "/compare/FFF7ED-EFF6FF"
          }
        ]
      }
    ],
    "sources": [
      {
        "text": "W3C: CSS Color 4",
        "href": "https://www.w3.org/TR/css-color-4/"
      },
      {
        "text": "Jonauskaite et al. (2020): Color-emotion associations across nations",
        "href": "https://www.psychologicalscience.org/journals/psychological-science/0956797620948810/"
      }
    ],
    "updated": "2026-09-13",
    "revision": "Rebuilt around explicit design choices, reproducible examples and documented limitations; retained the existing URL."
  },
  {
    "slug": "typography-color-guide",
    "title": "Choose Text Colors by Role and Background",
    "date": "2026-03-29",
    "readingTime": 2,
    "excerpt": "Test body copy, secondary text, links and disabled labels with concrete inputs rather than picking gray by appearance.",
    "tags": [
      "Design",
      "Accessibility",
      "UI"
    ],
    "author": "Color Match",
    "sections": [
      {
        "type": "paragraph",
        "content": "A text palette is a set of foreground/background decisions. Start with the body copy on the main reading surface, then test secondary text, links and messages where they actually appear. Type size, weight and content still need visual review after the numerical check."
      },
      {
        "type": "heading",
        "content": "Make hierarchy without hiding information"
      },
      {
        "type": "paragraph",
        "content": "For a white reading surface, compare #111827 body copy with #6B7280 secondary text. Use spacing, headings and weight to create hierarchy as well. A timestamp or field hint remains information even when it is visually secondary; do not lower its contrast simply because it is small."
      },
      {
        "type": "contrast-case",
        "fg": "#6B7280",
        "bg": "#FFFFFF",
        "content": "Secondary text candidate on white."
      },
      {
        "type": "contrast-case",
        "fg": "#9CA3AF",
        "bg": "#FFFFFF",
        "content": "A lighter candidate to check before using it for small text."
      },
      {
        "type": "heading",
        "content": "Use the correct text-size category"
      },
      {
        "type": "paragraph",
        "content": "The large-text exception begins at 18pt regular or 14pt bold, approximately 24px or 18.67px in CSS. A default 18px bold label is still below that threshold. Treat the checker result as one input when inspecting thin glyphs and the chosen font."
      },
      {
        "type": "heading",
        "content": "Give links a visible cue"
      },
      {
        "type": "code",
        "content": ".article { color: #111827; background: #FFFFFF; }\n.article a { color: #1D4ED8; text-decoration: underline; }\n.article .metadata { color: #6B7280; }"
      },
      {
        "type": "paragraph",
        "content": "An underline gives an inline link a cue beyond hue. Keep it visible outside hover and test the link against the actual surface. If you remove underlines, you need another valid way to distinguish links; changing the cursor alone does not help someone who has not found the link."
      },
      {
        "type": "heading",
        "content": "Review exceptions carefully"
      },
      {
        "type": "paragraph",
        "content": "Inactive controls have contrast exceptions, but a low-contrast label is not automatically inactive. Loading information, helper text and placeholder instructions may still be needed. Pure black on white is allowed; use softer tones only when they meet your reading and contrast needs."
      },
      {
        "type": "paragraph",
        "content": "Inspect this link color and use its result link in your typography specification. Repeat on tinted callouts and dark surfaces.",
        "links": [
          {
            "text": "Inspect this link color",
            "href": "/contrast-checker?fg=1D4ED8&bg=FFFFFF"
          }
        ]
      }
    ],
    "sources": [
      {
        "text": "W3C: Contrast (Minimum)",
        "href": "https://www.w3.org/WAI/WCAG22/Understanding/contrast-minimum.html"
      },
      {
        "text": "W3C: Use of Color",
        "href": "https://www.w3.org/WAI/WCAG22/Understanding/use-of-color.html"
      }
    ],
    "updated": "2026-09-13",
    "revision": "Rechecked technical claims, replaced unsupported generalizations, and added reproducible examples and source links."
  },
  {
    "slug": "figma-to-css-color",
    "title": "From Figma Color Decisions to Tested CSS Tokens",
    "date": "2026-03-30",
    "readingTime": 2,
    "excerpt": "Carry values, profiles, opacity and semantic roles from design to code, then inspect the rendered component.",
    "tags": [
      "Design",
      "Development",
      "Color Models"
    ],
    "author": "Color Match",
    "sections": [
      {
        "type": "paragraph",
        "content": "A useful handoff contains more than a HEX list. Record the file color profile, layer opacity, intended background, token role and component state. Color Match works with opaque 8-bit sRGB values, so treat a Display P3 or translucent design as a conversion decision before using its HEX checker."
      },
      {
        "type": "heading",
        "content": "Record the profile and composition"
      },
      {
        "type": "paragraph",
        "content": "Figma supports sRGB and Display P3 file profiles. Agree which profile the web implementation targets and inspect any conversion. A sampled screenshot can include shadows, transparency, image content and antialiasing; prefer the original fill and opacity information when available."
      },
      {
        "type": "heading",
        "content": "Map design roles to CSS roles"
      },
      {
        "type": "code",
        "content": ":root {\n  --surface-default: #FFFFFF;\n  --text-default: #111827;\n  --action-default: #1D4ED8;\n  --text-on-action: #FFFFFF;\n}\n.card { background: var(--surface-default); color: var(--text-default); }\n.card button { background: var(--action-default); color: var(--text-on-action); }"
      },
      {
        "type": "paragraph",
        "content": "Use a handoff row for each alias: design variable, CSS variable, light-mode value, dark-mode value, and intended partner. Two roles may share a HEX today and need to diverge later. Keeping their role names separate makes that change explicit."
      },
      {
        "type": "heading",
        "content": "Verify the actual browser result"
      },
      {
        "type": "contrast-case",
        "fg": "#111827",
        "bg": "#FFFFFF",
        "content": "Body text and default surface in the handoff."
      },
      {
        "type": "contrast-case",
        "fg": "#FFFFFF",
        "bg": "#1D4ED8",
        "content": "Action label and button surface in the handoff."
      },
      {
        "type": "paragraph",
        "content": "Use browser computed styles to check that the winning declarations match the handoff. If the label inherits opacity from a parent, these opaque checks no longer describe the composed result. Inspect narrow layouts and keyboard focus; a correct color value does not fix an obscured label."
      },
      {
        "type": "heading",
        "content": "Close the handoff loop"
      },
      {
        "type": "paragraph",
        "content": "Open the handoff palette in Studio and export CSS as a starting point. Rename or alias the generated values to the agreed roles, then send the component URL and recorded pairs back for review. No direct Figma account connection is needed for this manual workflow.",
        "links": [
          {
            "text": "Open the handoff palette in Studio",
            "href": "/studio/FFFFFF-111827-1D4ED8"
          }
        ]
      }
    ],
    "sources": [
      {
        "text": "Figma: Manage color profiles in design files",
        "href": "https://help.figma.com/hc/en-us/articles/360039825114-Manage-color-profiles-in-design-files"
      },
      {
        "text": "W3C: CSS Color 4",
        "href": "https://www.w3.org/TR/css-color-4/"
      }
    ],
    "updated": "2026-09-13",
    "revision": "Rechecked technical claims, replaced unsupported generalizations, and added reproducible examples and source links."
  },
  {
    "slug": "tailwind-color-system",
    "title": "Export Color Tokens for Tailwind CSS v3 and v4",
    "date": "2026-03-31",
    "readingTime": 2,
    "excerpt": "Use the correct Tailwind configuration format and test a real button before treating a generated scale as a design system.",
    "tags": [
      "Development",
      "Design",
      "UI"
    ],
    "author": "Color Match",
    "sections": [
      {
        "type": "paragraph",
        "content": "Choose an export that matches the Tailwind version in your application. Color Match offers separate v3 and v4 outputs because their configuration surfaces differ. The examples here use one brand color and one text partner, so you can verify a small change before importing an entire scale."
      },
      {
        "type": "heading",
        "content": "Tailwind v3: extend theme colors"
      },
      {
        "type": "code",
        "content": "// tailwind.config.cjs\nmodule.exports = {\n  theme: {\n    extend: {\n      colors: {\n        brand: { DEFAULT: \"#1D4ED8\" },\n        \"on-brand\": \"#FFFFFF\"\n      }\n    }\n  }\n};"
      },
      {
        "type": "paragraph",
        "content": "The v3 extend object adds these keys while retaining the existing palette. Replacing theme.colors has different consequences for utilities that rely on default colors. If your project uses ES modules, use its supported configuration extension or export syntax consistently."
      },
      {
        "type": "heading",
        "content": "Tailwind v4: declare theme variables"
      },
      {
        "type": "code",
        "content": "@import \"tailwindcss\";\n@theme {\n  --color-brand: #1D4ED8;\n  --color-on-brand: #FFFFFF;\n}"
      },
      {
        "type": "paragraph",
        "content": "In a v4 project these theme variables expose matching color utilities. Keep the same explicit semantic distinction between the surface and the text; the variable name itself supplies no readability guarantee."
      },
      {
        "type": "heading",
        "content": "Render a component before broad adoption"
      },
      {
        "type": "code",
        "content": "<button class=\"bg-brand text-on-brand px-4 py-2 rounded-lg\">\n  Save changes\n</button>"
      },
      {
        "type": "contrast-case",
        "fg": "#FFFFFF",
        "bg": "#1D4ED8",
        "content": "The sample button label and background."
      },
      {
        "type": "paragraph",
        "content": "Inspect computed styles after compiling your own project. Test focus visibility and each hover or disabled treatment you add. A lighter tint with the same white label can fail even when this base color passes. The generated tint/shade labels are two scales, not automatically the conventional 50-950 brand scale."
      },
      {
        "type": "heading",
        "content": "Keep imports reviewable"
      },
      {
        "type": "paragraph",
        "content": "Open a blue-and-white palette in Studio and choose the matching Tailwind export. Commit the generated values with the component example and its paired test so a later color change has a clear review target.",
        "links": [
          {
            "text": "Open a blue-and-white palette in Studio",
            "href": "/studio/1D4ED8-FFFFFF"
          }
        ]
      }
    ],
    "sources": [
      {
        "text": "Tailwind v3: Customizing Colors",
        "href": "https://v3.tailwindcss.com/docs/customizing-colors"
      },
      {
        "text": "Tailwind v4: Theme Variables",
        "href": "https://tailwindcss.com/docs/theme"
      }
    ],
    "updated": "2026-09-13",
    "revision": "Rechecked technical claims, replaced unsupported generalizations, and added reproducible examples and source links."
  },
  {
    "slug": "gradient-design-guide",
    "title": "CSS Gradients: Interpolation, Stops and Readable Text",
    "date": "2026-04-01",
    "readingTime": 2,
    "excerpt": "Compare interpolation spaces and build a gradient with a controlled text surface instead of trusting endpoint contrast.",
    "tags": [
      "Design",
      "Development",
      "UI"
    ],
    "author": "Color Match",
    "sections": [
      {
        "type": "paragraph",
        "content": "A gradient is a sequence of colors between stops. Its intermediate colors depend on the interpolation space, not only its endpoints. Review the result at its actual dimensions because a narrow button and a wide banner expose different parts of the transition."
      },
      {
        "type": "heading",
        "content": "Make the interpolation choice visible"
      },
      {
        "type": "code",
        "content": ".gradient-srgb { background: linear-gradient(90deg in srgb, #FF0000, #0000FF); }\n.gradient-oklab { background: linear-gradient(90deg in oklab, #FF0000, #0000FF); }"
      },
      {
        "type": "paragraph",
        "content": "Render these next to one another with the same dimensions. They demonstrate different interpolation paths; neither is an automatic quality score. Choose the path that fits the design and preserve a conventional fallback if your supported browser set needs it."
      },
      {
        "type": "heading",
        "content": "Control a midpoint when the content needs it"
      },
      {
        "type": "code",
        "content": ".hero {\n  background: linear-gradient(90deg, #172554 0%, #7C3AED 55%, #FBCFE8 100%);\n}\n.hero-copy {\n  color: #FFFFFF;\n  background: #172554;\n  padding: 1rem;\n}"
      },
      {
        "type": "paragraph",
        "content": "The pale endpoint of this hero makes white overlay text risky. The opaque navy text panel gives the copy a controlled surface. Its size and placement should follow the content; a transparent shadow under text is a different composition and needs another check."
      },
      {
        "type": "heading",
        "content": "Test text where it is drawn"
      },
      {
        "type": "contrast-case",
        "fg": "#FFFFFF",
        "bg": "#172554",
        "content": "White copy on the opaque navy panel."
      },
      {
        "type": "contrast-case",
        "fg": "#FFFFFF",
        "bg": "#FBCFE8",
        "content": "White copy would meet the pale endpoint if the panel were removed."
      },
      {
        "type": "paragraph",
        "content": "Checking the endpoints alone is not a general gradient audit. Inspect every background region beneath the text, including responsive positions, images and animation. Sampling a few positions can find failures but cannot prove that every point passes. The opaque panel simplifies the condition being measured."
      },
      {
        "type": "heading",
        "content": "Review rendering limits"
      },
      {
        "type": "paragraph",
        "content": "Open the hero colors in Studio to inspect the palette, then test the real gradient in the consuming page. Screenshots can help review banding and composition, but preserve the CSS values as the reproducible source.",
        "links": [
          {
            "text": "Open the hero colors in Studio",
            "href": "/studio/172554-7C3AED-FBCFE8-FFFFFF"
          }
        ]
      }
    ],
    "sources": [
      {
        "text": "MDN: linear-gradient()",
        "href": "https://developer.mozilla.org/en-US/docs/Web/CSS/gradient/linear-gradient"
      },
      {
        "text": "W3C: CSS Color 4 interpolation",
        "href": "https://www.w3.org/TR/css-color-4/"
      }
    ],
    "updated": "2026-09-13",
    "revision": "Rebuilt around explicit design choices, reproducible examples and documented limitations; retained the existing URL."
  },
  {
    "slug": "color-contrast-ui-components",
    "title": "UI Contrast: Check Labels, Boundaries and States Separately",
    "date": "2026-04-02",
    "readingTime": 3,
    "excerpt": "Work through an input and a button without confusing text contrast with the contrast of required visual cues.",
    "tags": [
      "Accessibility",
      "WCAG",
      "Development"
    ],
    "author": "Color Match",
    "sections": [
      {
        "type": "paragraph",
        "content": "A component rarely has only one contrast relationship. An input can contain a label, typed value, hint, boundary and focus indicator. A button has a label, fill, surrounding surface and state cues. Break the component into these parts before deciding what to measure."
      },
      {
        "type": "heading",
        "content": "Example: a text input on a white form"
      },
      {
        "type": "contrast-case",
        "content": "A gray border candidate against the surrounding white surface. The ratio is calculated below; the text badges refer only to text use.",
        "fg": "9CA3AF",
        "bg": "FFFFFF"
      },
      {
        "type": "paragraph",
        "content": "If this border is needed to identify the input, evaluate it as that visual cue rather than applying the normal-text threshold to the border. Non-text contrast generally requires 3:1 for visual information needed to identify controls and states. Not every decorative boundary is required to meet it; the role and surrounding information matter."
      },
      {
        "type": "paragraph",
        "content": "Now inspect the typed value and helper message independently. Even if the border meets its requirement, the helper text can still fail. Conversely, a strong label does not resolve an invisible required input boundary."
      },
      {
        "type": "heading",
        "content": "Example: a filled button"
      },
      {
        "type": "contrast-case",
        "content": "The white label against a blue fill is a text pair.",
        "fg": "FFFFFF",
        "bg": "2563EB"
      },
      {
        "type": "paragraph",
        "content": "Next inspect how the button is identified on the page surface. A border is not universally required when the control can be identified without it. If you add a focus ring, check it where it appears: outside the button, inside the button, or across both edges. The relevant adjacent colors depend on that placement."
      },
      {
        "type": "heading",
        "content": "Compare states with their surroundings"
      },
      {
        "type": "paragraph",
        "content": "For a hover style, recheck the label against the new fill. For a selected checkbox, check the mark against the box. You do not automatically need 3:1 between the default and hover colors just because they are different states; they may never be adjacent. You do need the meaningful information to remain identifiable in the state being displayed."
      },
      {
        "type": "heading",
        "content": "Keep a component test sheet"
      },
      {
        "type": "list",
        "content": "",
        "items": [
          "Input default: value/surface, hint/surface and required boundary/adjacent surface.",
          "Input focus: visible focus cue and each relevant adjacent surface.",
          "Button default and hover: label/fill in each state.",
          "Selected and error states: readable labels plus a cue that does not rely solely on hue."
        ]
      },
      {
        "type": "paragraph",
        "content": "Disabled controls have specific exceptions in the criteria; treat these as scoped requirements, not a reason to make all secondary content faint. A helper message, read-only value or low-priority action is not necessarily an inactive control."
      },
      {
        "type": "paragraph",
        "content": "Load any of these pairs in the contrast checker and choose the target for the specific check. Its text pass labels are not a verdict on the whole component.",
        "links": [
          {
            "text": "contrast checker",
            "href": "/contrast-checker"
          }
        ]
      },
      {
        "type": "paragraph",
        "content": "Finish with keyboard and visual inspection in the rendered interface. Contrast arithmetic cannot confirm that focus is present, that the control has an accessible name, or that the error message is associated with its field."
      }
    ],
    "sources": [
      {
        "text": "W3C: Contrast (Minimum), WCAG 2.2",
        "href": "https://www.w3.org/WAI/WCAG22/Understanding/contrast-minimum.html"
      },
      {
        "text": "W3C: Non-text Contrast",
        "href": "https://www.w3.org/WAI/WCAG22/Understanding/non-text-contrast.html"
      },
      {
        "text": "W3C: Use of Color",
        "href": "https://www.w3.org/WAI/WCAG22/Understanding/use-of-color.html"
      }
    ],
    "revision": "Corrected blanket UI contrast claims; added separate label, border and state checks.",
    "updated": "2026-09-13"
  },
  {
    "slug": "apca-vs-wcag-contrast",
    "title": "APCA and WCAG Contrast: Why the Numbers Differ",
    "date": "2026-04-05",
    "readingTime": 3,
    "excerpt": "Compare the two calculations with exact color pairs, understand APCA polarity, and keep WCAG 2.2 decisions separate from supplementary Lc measurements.",
    "tags": [
      "Accessibility",
      "WCAG"
    ],
    "author": "Color Match",
    "sections": [
      {
        "type": "paragraph",
        "content": "Color Match shows a WCAG contrast ratio and a signed APCA Lc value. They measure color contrast differently and their numbers cannot be substituted for one another. If your task is checking a WCAG 2.2 text requirement, make that decision with the WCAG ratio. Use APCA as additional information when investigating typography."
      },
      {
        "type": "heading",
        "content": "A controlled black-and-white comparison"
      },
      {
        "type": "contrast-case",
        "content": "Black text on white: a reference pair for both calculations.",
        "fg": "000000",
        "bg": "FFFFFF"
      },
      {
        "type": "contrast-case",
        "content": "The reversed pair: the WCAG ratio stays the same, while APCA changes sign and magnitude.",
        "fg": "FFFFFF",
        "bg": "000000"
      },
      {
        "type": "paragraph",
        "content": "The two WCAG results match because its ratio puts the lighter color in the numerator regardless of which color is text. APCA distinguishes dark-on-light from light-on-dark text. In the implementation used here, positive Lc indicates dark text on a lighter background; negative Lc indicates the reverse. The sign does not mean pass or fail."
      },
      {
        "type": "heading",
        "content": "Do not translate 4.5:1 into one universal Lc target"
      },
      {
        "type": "paragraph",
        "content": "A ratio threshold and an Lc value belong to different calculations. There is no fixed multiplier that converts every WCAG ratio to APCA, and an Lc recommendation must be interpreted with typography. The same pair can feel different at small, thin body text and at a large, heavy display heading."
      },
      {
        "type": "contrast-case",
        "content": "A near-threshold WCAG pair with its independently calculated APCA value.",
        "fg": "777777",
        "bg": "FFFFFF"
      },
      {
        "type": "paragraph",
        "content": "Try the pair in the checker, swap the colors and compare both outputs. This is a useful diagnostic experiment: the WCAG value should remain unchanged, while the APCA output can change in both sign and size. If only the label changes, the implementation may be taking an absolute value too early."
      },
      {
        "type": "heading",
        "content": "Which implementation is documented here?"
      },
      {
        "type": "paragraph",
        "content": "The calculation follows the published APCA 0.0.98G sRGB constants and transfer behavior. It applies the 2.4 power transform, a single low-luminance clamp, and the reference low-contrast cutoffs. Reference checks cover black/white, reversed polarity, middle gray and saturated red. A version label matters because an algorithm or recommendation table can evolve."
      },
      {
        "type": "heading",
        "content": "A practical decision record"
      },
      {
        "type": "list",
        "content": "",
        "items": [
          "Write down the exact text color, background, typeface, size and weight.",
          "Record the WCAG ratio and required text category for the conformance task.",
          "If using APCA, preserve foreground/background order and the signed Lc output.",
          "Review the actual typography instead of treating either number as a complete readability score."
        ]
      },
      {
        "type": "paragraph",
        "content": "APCA is not presented on this site as a WCAG 2.2 pass criterion or as proof of WCAG 3 conformance. The checker deliberately keeps its supplementary result separate from AA and AAA labels."
      },
      {
        "type": "paragraph",
        "content": "See our calculation methods for formulas, reference values and the limitations of opaque sRGB inputs.",
        "links": [
          {
            "text": "calculation methods",
            "href": "/methodology"
          }
        ]
      }
    ],
    "sources": [
      {
        "text": "W3C: Contrast (Minimum), WCAG 2.2",
        "href": "https://www.w3.org/WAI/WCAG22/Understanding/contrast-minimum.html"
      },
      {
        "text": "APCA author documentation and reference implementation",
        "href": "https://github.com/Myndex/apca-w3"
      }
    ],
    "revision": "Corrected APCA math and status claims; added signed reference pairs and interpretation limits.",
    "updated": "2026-09-13"
  },
  {
    "slug": "oklch-color-space-guide",
    "title": "OKLCH in CSS: Lightness, Chroma and Gamut",
    "date": "2026-04-05",
    "readingTime": 2,
    "excerpt": "Understand OKLCH units, compare it with HSL, and keep sRGB conversion limits visible in a practical palette workflow.",
    "tags": [
      "Development",
      "Color Models"
    ],
    "author": "Color Match",
    "sections": [
      {
        "type": "paragraph",
        "content": "OKLCH expresses Oklab in cylindrical coordinates: lightness, chroma and hue angle. It is useful for exploring a tone scale while holding hue approximately stable. It is not a contrast ratio, and a fixed lightness difference does not certify readable text."
      },
      {
        "type": "heading",
        "content": "Read the CSS units correctly"
      },
      {
        "type": "code",
        "content": ".accent {\n  color: #6366F1;\n  color: oklch(60% 0.20 280);\n}"
      },
      {
        "type": "paragraph",
        "content": "The first declaration is an explicit fallback; the second is a separate example color, not a claim that these two representations are identical. In CSS, 60% lightness corresponds to 0.6. Chroma 0.20 is a raw number. Color Match stores lightness on a 0-100 scale and chroma multiplied by 100 internally, then divides chroma for CSS output."
      },
      {
        "type": "heading",
        "content": "Distinguish appearance from luminance"
      },
      {
        "type": "paragraph",
        "content": "Oklab was designed to improve perceptual behavior compared with simple RGB manipulations. HSL lightness is based on RGB extrema, so two fully saturated HSL colors with the same L can be very different in contrast. Use the two examples below to see why a numeric lightness label alone is insufficient."
      },
      {
        "type": "contrast-case",
        "fg": "#FFFF00",
        "bg": "#FFFFFF",
        "content": "HSL yellow at 50% lightness against white."
      },
      {
        "type": "contrast-case",
        "fg": "#0000FF",
        "bg": "#FFFFFF",
        "content": "HSL blue at the same lightness against white."
      },
      {
        "type": "heading",
        "content": "Keep the target gamut explicit"
      },
      {
        "type": "paragraph",
        "content": "Some OKLCH coordinates fall outside sRGB. This tool converts to 8-bit sRGB for its HEX values and previews; clipping or conversion can alter hue and chroma. Do not describe that output as preservation of every Display P3 color. For wide-gamut production, inspect the browser rendering and retain the intended profile in your handoff."
      },
      {
        "type": "heading",
        "content": "Try a controlled scale"
      },
      {
        "type": "paragraph",
        "content": "Open the converter, enter a known HEX value, and inspect its calculated OKLCH form. Change one dimension at a time in your CSS, measure each finished foreground/background pair, and save both the requested value and the actual fallback.",
        "links": [
          {
            "text": "Open the converter",
            "href": "/converter"
          }
        ]
      }
    ],
    "sources": [
      {
        "text": "W3C: CSS Color 4",
        "href": "https://www.w3.org/TR/css-color-4/"
      },
      {
        "text": "Bjorn Ottosson: A perceptual color space for image processing",
        "href": "https://bottosson.github.io/posts/oklab/"
      },
      {
        "text": "MDN: oklch()",
        "href": "https://developer.mozilla.org/en-US/docs/Web/CSS/color_value/oklch"
      }
    ],
    "updated": "2026-09-13",
    "revision": "Rechecked technical claims, replaced unsupported generalizations, and added reproducible examples and source links."
  },
  {
    "slug": "how-to-compare-colors",
    "title": "How to Compare Two Colors for a Specific Design Role",
    "date": "2026-04-05",
    "readingTime": 3,
    "excerpt": "Separate visual difference from text contrast, compare exact values, and preserve both colors in a shareable decision.",
    "tags": [
      "Design",
      "UI"
    ],
    "author": "Color Match",
    "sections": [
      {
        "type": "paragraph",
        "content": "Before comparing two colors, decide what the comparison is for. Two brand candidates can be judged side by side; text and its surface need a contrast check; two chart categories need a way to distinguish meaning. One score cannot answer all three questions."
      },
      {
        "type": "heading",
        "content": "Keep the experiment controlled"
      },
      {
        "type": "paragraph",
        "content": "Open both colors at the same time with similar swatch sizes. Compare them on the surface where they will be used and keep that surface fixed while choosing. A tiny sample on a white page can give a different impression from a large panel surrounded by dark colors. The comparison tool preserves the two input values so the exercise can be repeated."
      },
      {
        "type": "paragraph",
        "content": "Open coral and teal in Compare Colors. Read the HEX, RGB and HSL values, then inspect the text previews in both directions.",
        "links": [
          {
            "text": "Compare Colors",
            "href": "/compare/FF6B6B-4ECDC4"
          }
        ]
      },
      {
        "type": "heading",
        "content": "Hue difference does not guarantee readable text"
      },
      {
        "type": "contrast-case",
        "content": "These are visually different hues. The computed text contrast can still be too low for the intended role.",
        "fg": "FF6B6B",
        "bg": "4ECDC4"
      },
      {
        "type": "paragraph",
        "content": "If you like both colors, assign them different jobs. For example, use one as a decorative accent and choose a separate dark text token. Do not assume the two accents need to become a foreground/background pair simply because they appear in the same palette."
      },
      {
        "type": "heading",
        "content": "Use the value readouts for different tasks"
      },
      {
        "type": "list",
        "content": "",
        "items": [
          "HEX and RGB identify the exact opaque sRGB sample for implementation and sharing.",
          "HSL helps describe hue, saturation and lightness adjustments, but equal HSL lightness is not equal relative luminance.",
          "A nearest color name helps browsing. It is not a guarantee that another library or print supplier uses the same color."
        ]
      },
      {
        "type": "heading",
        "content": "Move a text decision into the checker"
      },
      {
        "type": "paragraph",
        "content": "When the task is text readability, specify which color is the text, which is the surface, and how large and heavy the type is. Open the checker from the comparison, choose the requirement, and test a suggested adjustment. Swapping colors leaves the WCAG ratio unchanged, but it changes the design context and APCA polarity."
      },
      {
        "type": "heading",
        "content": "Document the outcome, not only the winner"
      },
      {
        "type": "paragraph",
        "content": "A useful handoff says: use coral for decoration, use the checked dark foreground for labels, and keep the pale surface behind it. Attach the comparison URL and the contrast-result URL. That is more actionable than saying that one color looks better, and it prevents the palette from being reused in an untested arrangement."
      },
      {
        "type": "paragraph",
        "content": "For chart categories, also test labels, patterns or shapes. Color contrast between two swatches does not establish that everyone can identify their meaning. For print, ask for a managed conversion and proof rather than treating the screen sample as a production ink specification."
      }
    ],
    "sources": [
      {
        "text": "W3C: Contrast (Minimum), WCAG 2.2",
        "href": "https://www.w3.org/WAI/WCAG22/Understanding/contrast-minimum.html"
      },
      {
        "text": "W3C: CSS Color Module Level 4",
        "href": "https://www.w3.org/TR/css-color-4/"
      }
    ],
    "revision": "Rebuilt around the restored two-color comparison tool and reproducible role-based checks.",
    "updated": "2026-09-13"
  },
  {
    "slug": "color-accessibility-checklist",
    "title": "A Practical Color Accessibility Review Checklist",
    "date": "2026-04-05",
    "readingTime": 2,
    "excerpt": "Record text, boundaries, focus and color-only meaning separately, with a worked form example and evidence you can hand to a reviewer.",
    "tags": [
      "Accessibility",
      "WCAG",
      "Design"
    ],
    "author": "Color Match",
    "sections": [
      {
        "type": "paragraph",
        "content": "Use this checklist to record a color review of one real screen. It covers color-related checks, not every WCAG requirement. Record the URL, component, state, foreground, background, font size and result. A passing swatch says little about an error message that disappears on focus."
      },
      {
        "type": "heading",
        "content": "1. Inventory the visible states"
      },
      {
        "type": "paragraph",
        "content": "For a sign-in form, inspect the heading, field label, placeholder, typed value, hint, error text and submit button. Repeat with an invalid field and while navigating by keyboard. Include the page surface outside the form; an input boundary can touch a different color than its text does."
      },
      {
        "type": "heading",
        "content": "2. Separate text from component boundaries"
      },
      {
        "type": "paragraph",
        "content": "W3C distinguishes text contrast from essential non-text information. For this example we use 4.5:1 for normal text and examine the field boundary separately at 3:1. A decorative divider is not automatically an essential boundary. Record why a border is needed to identify a field instead of applying the same rule to every line."
      },
      {
        "type": "contrast-case",
        "fg": "#777777",
        "bg": "#FFFFFF",
        "content": "Placeholder candidate: the numeric result below decides whether normal text passes."
      },
      {
        "type": "contrast-case",
        "fg": "#767676",
        "bg": "#FFFFFF",
        "content": "A nearby correction to compare with the original placeholder."
      },
      {
        "type": "heading",
        "content": "3. Check focus and meaning"
      },
      {
        "type": "paragraph",
        "content": "Tab to every control and make sure its focus remains visible, including under a sticky header. Do not label the WCAG 2.2 AAA Focus Appearance criterion as an AA rule. Describe errors in words, associate their text with the field, and add a visible cue beyond a red border. Test that the correction clears the error message appropriately."
      },
      {
        "type": "heading",
        "content": "4. Keep an audit record"
      },
      {
        "type": "code",
        "content": "Component: email field\nState: invalid + focused\nText: #B91C1C on #FFFFFF\nBoundary: #767676 on #FFFFFF\nMeaning: \"Enter a valid email address\" + error icon\nManual follow-up: keyboard focus, zoom, screen-reader announcement"
      },
      {
        "type": "paragraph",
        "content": "Open the error-text pair and save its result alongside your screen review. Recheck computed CSS after fixes and record any untested states.",
        "links": [
          {
            "text": "Open the error-text pair",
            "href": "/contrast-checker?fg=B91C1C&bg=FFFFFF"
          }
        ]
      }
    ],
    "sources": [
      {
        "text": "W3C: Contrast (Minimum)",
        "href": "https://www.w3.org/WAI/WCAG22/Understanding/contrast-minimum.html"
      },
      {
        "text": "W3C: Non-text Contrast",
        "href": "https://www.w3.org/WAI/WCAG22/Understanding/non-text-contrast.html"
      },
      {
        "text": "W3C: Use of Color",
        "href": "https://www.w3.org/WAI/WCAG22/Understanding/use-of-color.html"
      },
      {
        "text": "W3C: Focus Visible",
        "href": "https://www.w3.org/WAI/WCAG22/Understanding/focus-visible.html"
      },
      {
        "text": "W3C: Focus Appearance (AAA)",
        "href": "https://www.w3.org/WAI/WCAG22/Understanding/focus-appearance.html"
      }
    ],
    "updated": "2026-09-13",
    "revision": "Rechecked technical claims, replaced unsupported generalizations, and added reproducible examples and source links."
  },
  {
    "slug": "design-system-color-tokens",
    "title": "Design Color Tokens with Explicit Roles and Tested Pairs",
    "date": "2026-04-05",
    "readingTime": 2,
    "excerpt": "Build primitive, semantic and component tokens from a small palette, then export and test the roles that consume them.",
    "tags": [
      "Design",
      "Development"
    ],
    "author": "Color Match",
    "sections": [
      {
        "type": "paragraph",
        "content": "A useful color token tells a maintainer what may change together. A primitive names a value; a semantic token names a role. When a brand shade changes, inspect the semantic roles that depend on it rather than replacing every matching HEX string."
      },
      {
        "type": "heading",
        "content": "Write a small dependency chain"
      },
      {
        "type": "code",
        "content": ":root {\n  --blue-700: #1D4ED8;\n  --neutral-0: #FFFFFF;\n  --action-primary: var(--blue-700);\n  --text-on-action: var(--neutral-0);\n}\n.button-primary {\n  background: var(--action-primary);\n  color: var(--text-on-action);\n}"
      },
      {
        "type": "paragraph",
        "content": "The primitive blue is reusable. The action token commits it to an interactive surface. The text-on-action token records the pairing that makes the button readable. Avoid calling a color \"accessible-blue\": its contrast depends on its partner and role."
      },
      {
        "type": "heading",
        "content": "Record the pair, not a palette-wide score"
      },
      {
        "type": "contrast-case",
        "fg": "#FFFFFF",
        "bg": "#1D4ED8",
        "content": "Text-on-action / action-primary in the sample tokens."
      },
      {
        "type": "paragraph",
        "content": "Store the two inputs, target category and result with the component. If a dark theme changes either token, run the check again. Hover, pressed and focus styles deserve separate records because their neighboring surfaces may differ."
      },
      {
        "type": "heading",
        "content": "Choose a named interchange format"
      },
      {
        "type": "paragraph",
        "content": "CSS custom properties, a generic JSON object and DTCG tokens serve different consumers. DTCG 2025.10 represents an sRGB color as an object with colorSpace and normalized components. A string such as \"#1D4ED8\" alone is not that color-value object. Check what your importer expects rather than assuming every JSON export is interchangeable."
      },
      {
        "type": "code",
        "content": "{\n  \"action\": {\n    \"$type\": \"color\",\n    \"$value\": {\n      \"colorSpace\": \"srgb\",\n      \"components\": [0.11372549019607843, 0.3058823529411765, 0.8470588235294118],\n      \"alpha\": 1,\n      \"hex\": \"#1d4ed8\"\n    }\n  }\n}"
      },
      {
        "type": "heading",
        "content": "Export, import and inspect"
      },
      {
        "type": "paragraph",
        "content": "Open the sample palette in Studio, export the format your project uses, and add role aliases in that project. Generated tints and shades are raw choices; import success does not mean every resulting component passes its checks.",
        "links": [
          {
            "text": "Open the sample palette in Studio",
            "href": "/studio/1D4ED8-FFFFFF-111827"
          }
        ]
      }
    ],
    "sources": [
      {
        "text": "DTCG: Format Module 2025.10",
        "href": "https://www.designtokens.org/tr/2025.10/format/"
      },
      {
        "text": "DTCG: Color Module 2025.10",
        "href": "https://www.designtokens.org/tr/2025.10/color/"
      },
      {
        "text": "W3C: Contrast (Minimum)",
        "href": "https://www.w3.org/WAI/WCAG22/Understanding/contrast-minimum.html"
      }
    ],
    "updated": "2026-09-13",
    "revision": "Rechecked technical claims, replaced unsupported generalizations, and added reproducible examples and source links."
  },
  {
    "slug": "css-color-mix-guide",
    "title": "CSS color-mix(): Weights, Spaces and Tested Results",
    "date": "2026-04-05",
    "readingTime": 2,
    "excerpt": "Mix a tint or state color with explicit interpolation and check the final result rather than assuming the mix is accessible.",
    "tags": [
      "Development",
      "Color Models"
    ],
    "author": "Color Match",
    "sections": [
      {
        "type": "paragraph",
        "content": "color-mix() computes a mixture of two colors in a named color space. It is useful when a tint or surface should follow a base variable. The operation does not choose a text partner or evaluate contrast for you."
      },
      {
        "type": "heading",
        "content": "Start with a reproducible sRGB mixture"
      },
      {
        "type": "code",
        "content": ".tint {\n  background: #FF8080;\n  background: color-mix(in srgb, #FF0000 50%, #FFFFFF);\n}"
      },
      {
        "type": "paragraph",
        "content": "In this example the red channel stays at 255 and the green and blue channels lie halfway between 0 and 255. The eight-bit fallback rounds them to 128. A browser can retain fractional precision internally, so the fallback is a quantized representation, not an assertion that every serialized value is identical."
      },
      {
        "type": "heading",
        "content": "Keep the weights clear"
      },
      {
        "type": "paragraph",
        "content": "When one percentage is omitted, it complements the supplied weight. Percentages can also be normalized when their sum differs from 100%, and a sum below 100% affects alpha. For an introductory opaque tint, use explicit weights summing to 100% so the intended mixture is easy to review."
      },
      {
        "type": "heading",
        "content": "Choose the interpolation space deliberately"
      },
      {
        "type": "code",
        "content": ".srgb-mix { color: color-mix(in srgb, red 50%, blue 50%); }\n.oklab-mix { color: color-mix(in oklab, red 50%, blue 50%); }"
      },
      {
        "type": "paragraph",
        "content": "The different intermediate colors reflect different interpolation coordinates. Use the browser to view the two examples side by side. If you need an exact 8-bit handoff, record the computed or intentionally chosen sRGB fallback rather than guessing it from the input hue names."
      },
      {
        "type": "heading",
        "content": "Retest a derived state"
      },
      {
        "type": "contrast-case",
        "fg": "#FFFFFF",
        "bg": "#FF8080",
        "content": "White text on the quantized red/white tint."
      },
      {
        "type": "contrast-case",
        "fg": "#000000",
        "bg": "#FF8080",
        "content": "Black text on the same tint."
      },
      {
        "type": "paragraph",
        "content": "These results demonstrate why a tint cannot inherit its base color's text decision without checking. A hover mix may also change the contrast of a border or focus cue against adjacent surfaces."
      },
      {
        "type": "paragraph",
        "content": "Open the tint pair in the checker and keep the result with the component CSS. Then test the dynamic mixture in the browser when the base custom property changes.",
        "links": [
          {
            "text": "Open the tint pair in the checker",
            "href": "/contrast-checker?fg=FFFFFF&bg=FF8080"
          }
        ]
      }
    ],
    "sources": [
      {
        "text": "W3C: CSS Color 5",
        "href": "https://www.w3.org/TR/css-color-5/"
      },
      {
        "text": "MDN: color-mix()",
        "href": "https://developer.mozilla.org/en-US/docs/Web/CSS/color_value/color-mix"
      }
    ],
    "updated": "2026-09-13",
    "revision": "Rebuilt around explicit design choices, reproducible examples and documented limitations; retained the existing URL."
  },
  {
    "slug": "hex-to-rgb-conversion",
    "title": "HEX to RGB: Convert Bytes, Expand Shorthand and Handle Alpha",
    "date": "2026-04-06",
    "readingTime": 3,
    "excerpt": "Convert #228BE6 by hand, verify the result in a tool, and avoid confusing shorthand and transparency with six-digit color values.",
    "tags": [
      "Color Models",
      "Development"
    ],
    "author": "Color Match",
    "sections": [
      {
        "type": "paragraph",
        "content": "A six-digit HEX color and an rgb() value can describe the same opaque sRGB color. HEX writes each channel in base 16; ordinary integer RGB writes it in base 10. Converting the notation does not make the color brighter, increase its gamut or prepare it for a printer."
      },
      {
        "type": "heading",
        "content": "Work through #228BE6"
      },
      {
        "type": "paragraph",
        "content": "Split the six digits into three pairs: 22, 8B and E6. In hexadecimal, A through F mean 10 through 15. For each pair, multiply the first digit by 16 and add the second. Red is 2*16+2 = 34. Green is 8*16+11 = 139. Blue is 14*16+6 = 230. The result is rgb(34 139 230)."
      },
      {
        "type": "paragraph",
        "content": "To reverse the conversion, divide a channel by 16. The integer quotient is the first HEX digit and the remainder is the second. For 139, the quotient is 8 and the remainder is 11, so the pair is 8B. Keep leading zeroes: decimal 5 is 05 as a channel pair, not a single 5 in a six-digit code."
      },
      {
        "type": "paragraph",
        "content": "Enter #228BE6 in the color converter and compare each channel with the hand calculation.",
        "links": [
          {
            "text": "color converter",
            "href": "/converter"
          }
        ]
      },
      {
        "type": "heading",
        "content": "Expand shorthand before splitting"
      },
      {
        "type": "paragraph",
        "content": "Three-digit #3AF expands by repeating each digit: #33AAFF. Its channel values are 51, 170 and 255. It does not mean #0003AF, and it does not become #3AF3AF. A six-digit code can shorten only when both digits in every channel pair match."
      },
      {
        "type": "list",
        "content": "",
        "items": [
          "#000000 becomes #000 and represents rgb(0 0 0).",
          "#FFFFFF becomes #FFF and represents rgb(255 255 255).",
          "#AABBCC becomes #ABC; #AABBCD cannot use that shorthand."
        ]
      },
      {
        "type": "heading",
        "content": "Alpha changes the task"
      },
      {
        "type": "paragraph",
        "content": "CSS also supports alpha-bearing hexadecimal notation. Eight digits use RRGGBBAA, so the final pair is transparency. Four-digit RGBA shorthand repeats each digit in the same way. An alpha byte of 80 is 128/255, approximately 0.502; it is not exactly one half."
      },
      {
        "type": "paragraph",
        "content": "Color Match contrast inputs currently accept opaque three- or six-digit values. Do not silently remove an alpha channel and test the remaining color: translucent text is displayed as a mixture with what is behind it. Determine the resulting visible colors before evaluating that contrast pair."
      },
      {
        "type": "heading",
        "content": "Validate before converting"
      },
      {
        "type": "list",
        "content": "",
        "items": [
          "Remove only an optional leading # and surrounding whitespace.",
          "Require valid hexadecimal digits; G is not a HEX digit.",
          "Check the supported length rather than padding arbitrary short input.",
          "When using numeric RGB bytes, require integers from 0 through 255 for this conversion."
        ]
      },
      {
        "type": "heading",
        "content": "After conversion, test the intended use"
      },
      {
        "type": "contrast-case",
        "content": "The worked color as text against white. Matching notation does not guarantee the pair meets the desired text requirement.",
        "fg": "228BE6",
        "bg": "FFFFFF"
      },
      {
        "type": "paragraph",
        "content": "Use the comparison tool when choosing between two samples, or the contrast checker when deciding whether this exact sample works as a foreground. Keep the original color space with the values when handing off wide-gamut design assets; changing notation alone is not a color-space conversion."
      }
    ],
    "sources": [
      {
        "text": "W3C: CSS Color Module Level 4",
        "href": "https://www.w3.org/TR/css-color-4/"
      }
    ],
    "revision": "Added worked byte calculations, shorthand and alpha distinctions, and input validation examples.",
    "updated": "2026-09-13"
  },
  {
    "slug": "web-design-color-palettes-2026",
    "title": "Five Web Palette Starting Points with Explicit Roles",
    "date": "2026-04-06",
    "readingTime": 2,
    "excerpt": "Explore small palettes for an article, dashboard, portfolio, shop and dark workspace, then test the roles before exporting.",
    "tags": [
      "Design",
      "Branding",
      "UI"
    ],
    "author": "Color Match",
    "sections": [
      {
        "type": "paragraph",
        "content": "A palette is easier to use when it starts from a screen. These five examples are constructed starting points, not measured design trends for 2026. Their labels describe the intended use in this exercise, and every added component still needs its own checks."
      },
      {
        "type": "heading",
        "content": "1. An article with a warm canvas"
      },
      {
        "type": "code",
        "content": "Canvas #FFF7ED\nBody #172554\nAccent #FF7F50\nText on accent #000000"
      },
      {
        "type": "paragraph",
        "content": "Open the article palette to inspect headings and body text first. Use the coral accent sparingly for decoration or a tested button; do not assume it works as small text on cream.",
        "links": [
          {
            "text": "Open the article palette",
            "href": "/studio/FFF7ED-172554-FF7F50-000000"
          }
        ]
      },
      {
        "type": "heading",
        "content": "2. A dashboard with quiet surfaces"
      },
      {
        "type": "code",
        "content": "Canvas #F8FAFC\nCard #FFFFFF\nBody #0F172A\nAction #1D4ED8\nAction text #FFFFFF"
      },
      {
        "type": "paragraph",
        "content": "Open the dashboard palette and check a card, selected navigation item and form field. The near-white surfaces will need stronger boundaries where users depend on those edges.",
        "links": [
          {
            "text": "Open the dashboard palette",
            "href": "/studio/F8FAFC-FFFFFF-0F172A-1D4ED8"
          }
        ]
      },
      {
        "type": "heading",
        "content": "3. A portfolio with a pale lavender panel"
      },
      {
        "type": "contrast-case",
        "fg": "#3B0764",
        "bg": "#F3E8FF",
        "content": "Portfolio heading and panel."
      },
      {
        "type": "paragraph",
        "content": "Open the portfolio palette and inspect a project card with a real screenshot. The image may dominate the visual balance; keep the text panel controlled.",
        "links": [
          {
            "text": "Open the portfolio palette",
            "href": "/studio/F3E8FF-3B0764-A855F7-FFFFFF"
          }
        ]
      },
      {
        "type": "heading",
        "content": "4. A shop with warm product surfaces"
      },
      {
        "type": "contrast-case",
        "fg": "#431407",
        "bg": "#FFEDD5",
        "content": "Product information on a warm surface."
      },
      {
        "type": "paragraph",
        "content": "Open the shop palette to inspect price, availability and purchase controls. Use explicit availability labels; the decorative accent should not be the only stock indicator.",
        "links": [
          {
            "text": "Open the shop palette",
            "href": "/studio/FFEDD5-431407-C2410C-FFFFFF"
          }
        ]
      },
      {
        "type": "heading",
        "content": "5. A dark workspace"
      },
      {
        "type": "paragraph",
        "content": "Open the dark workspace palette and inspect body copy on both canvas and cards. Keep light action colors paired with a dark label and recheck focus against neighboring surfaces.",
        "links": [
          {
            "text": "Open the dark workspace palette",
            "href": "/studio/111827-1F2937-F9FAFB-93C5FD"
          }
        ]
      },
      {
        "type": "heading",
        "content": "Document your chosen roles"
      },
      {
        "type": "paragraph",
        "content": "Copy the actual values with role names and the pair results you verified. The Studio export supplies palette values; add semantic aliases in the consuming project. Review the final CSS and component states after import instead of treating an exported file as a full design system."
      }
    ],
    "sources": [
      {
        "text": "W3C: Contrast (Minimum)",
        "href": "https://www.w3.org/WAI/WCAG22/Understanding/contrast-minimum.html"
      },
      {
        "text": "W3C: Non-text Contrast",
        "href": "https://www.w3.org/WAI/WCAG22/Understanding/non-text-contrast.html"
      }
    ],
    "updated": "2026-09-13",
    "revision": "Rebuilt around explicit design choices, reproducible examples and documented limitations; retained the existing URL."
  },
  {
    "slug": "red-color-meaning",
    "title": "Using Red in Interfaces: Meaning, Roles and Contrast",
    "date": "2026-04-06",
    "readingTime": 2,
    "excerpt": "Keep error and destructive-action meanings explicit, and test red as text and as a surface with concrete examples.",
    "tags": [
      "Psychology",
      "Color Theory",
      "Design"
    ],
    "author": "Color Match",
    "sections": [
      {
        "type": "paragraph",
        "content": "Red can be a decorative brand accent, an error cue or a destructive-action surface. Decide which role it has in your interface before choosing its text partner. Do not rely on a universal claim that red means danger, luck or urgency to every reader."
      },
      {
        "type": "heading",
        "content": "Separate association from product behavior"
      },
      {
        "type": "paragraph",
        "content": "Cross-national color-emotion research reports both common associations and contextual variation. It does not establish that a red purchase button increases conversion or that a single cultural meaning applies to every participant. Treat audience interpretation as something to investigate with the actual words and task."
      },
      {
        "type": "heading",
        "content": "Test red as text"
      },
      {
        "type": "contrast-case",
        "fg": "#FF0000",
        "bg": "#FFFFFF",
        "content": "Pure red on white: inspect its normal-text result."
      },
      {
        "type": "contrast-case",
        "fg": "#B91C1C",
        "bg": "#FFFFFF",
        "content": "Darker red candidate for an error message."
      },
      {
        "type": "paragraph",
        "content": "For an email error, use the darker candidate with a message such as \"Enter an email address with an @ sign.\" Associate it with the field and include a visible error cue. A red outline alone requires the user to infer what went wrong."
      },
      {
        "type": "heading",
        "content": "Test red as a button background"
      },
      {
        "type": "contrast-case",
        "fg": "#FFFFFF",
        "bg": "#B91C1C",
        "content": "White label on the darker red action surface."
      },
      {
        "type": "code",
        "content": ".delete-button { color: #FFFFFF; background: #B91C1C; }\n.delete-button:hover { background: #991B1B; }"
      },
      {
        "type": "paragraph",
        "content": "Use an explicit label such as \"Delete draft\" and review hover and focus separately. Color does not replace a reversible workflow or an appropriate confirmation for destructive work. Avoid applying red to unrelated decorative elements next to the action when that makes its role ambiguous."
      },
      {
        "type": "heading",
        "content": "Document your convention"
      },
      {
        "type": "paragraph",
        "content": "Open the red role palette in Studio. Record where each red is allowed and which label or icon accompanies it. Keep the convention consistent and test it with the audience who will use the interface.",
        "links": [
          {
            "text": "Open the red role palette in Studio",
            "href": "/studio/FF0000-B91C1C-991B1B-FFFFFF"
          }
        ]
      }
    ],
    "sources": [
      {
        "text": "Jonauskaite et al. (2020): Color-emotion associations across nations",
        "href": "https://www.psychologicalscience.org/journals/psychological-science/0956797620948810/"
      },
      {
        "text": "W3C: Use of Color",
        "href": "https://www.w3.org/WAI/WCAG22/Understanding/use-of-color.html"
      },
      {
        "text": "W3C: Contrast (Minimum)",
        "href": "https://www.w3.org/WAI/WCAG22/Understanding/contrast-minimum.html"
      }
    ],
    "updated": "2026-09-13",
    "revision": "Rebuilt around explicit design choices, reproducible examples and documented limitations; retained the existing URL."
  },
  {
    "slug": "how-to-check-color-contrast",
    "title": "How to Audit Text Contrast on a Real Web Page",
    "date": "2026-04-06",
    "readingTime": 3,
    "excerpt": "Find the actual foreground and surface, test interactive states, fix a failure and record evidence that another person can reproduce.",
    "tags": [
      "Accessibility",
      "WCAG",
      "Development"
    ],
    "author": "Color Match",
    "sections": [
      {
        "type": "paragraph",
        "content": "A pasted HEX pair is only as useful as the colors you extracted. A design file may show one surface while the shipped component inherits another. Start with one real element in the browser, record its state, and identify the colors that actually meet there."
      },
      {
        "type": "heading",
        "content": "1. Make an inventory by role"
      },
      {
        "type": "list",
        "content": "",
        "items": [
          "Begin with body text, navigation, form labels, placeholder text and helper messages.",
          "Repeat the check for hover, keyboard focus, selected, error and dark-mode states.",
          "Record a page URL and a recognizable element label. Keep unpublished or personal information out of publicly shared links."
        ]
      },
      {
        "type": "paragraph",
        "content": "For a form, write down the label, input value, hint and error message separately. They may use different tokens even though they sit in the same component. A passing label does not establish that a pale helper message passes."
      },
      {
        "type": "heading",
        "content": "2. Inspect computed colors"
      },
      {
        "type": "paragraph",
        "content": "Use browser developer tools to inspect the element. Record the computed text color, font size and weight, and identify the visible background beneath it. Check ancestor opacity and overlays. Avoid sampling the antialiased fringe of a letter as though it were the intended foreground; it contains a mixture of text and background."
      },
      {
        "type": "paragraph",
        "content": "For ordinary opaque colors, translate RGB to HEX if needed and enter the values in the checker. For gradients and images, inspect the changing background where the text appears. A single sampled point cannot stand in for the whole word or every responsive crop."
      },
      {
        "type": "heading",
        "content": "3. Test and preserve a failing example"
      },
      {
        "type": "contrast-case",
        "content": "A pale helper-text example. The computed result below makes the failure reproducible without depending on a screenshot.",
        "fg": "9CA3AF",
        "bg": "FFFFFF"
      },
      {
        "type": "paragraph",
        "content": "Use the contrast checker to load these exact values. Its result link stores the two colors, so a reviewer can reopen the same calculation.",
        "links": [
          {
            "text": "contrast checker",
            "href": "/contrast-checker?fg=9CA3AF&bg=FFFFFF"
          }
        ]
      },
      {
        "type": "heading",
        "content": "4. Make one deliberate change"
      },
      {
        "type": "paragraph",
        "content": "Keep the background fixed and apply a suggested foreground. Review the new text beside surrounding body copy, links and disabled content. Then paste the exported CSS into the real component and inspect its computed style again. Token aliases, specificity and opacity can otherwise leave the shipped appearance different from the tested pair."
      },
      {
        "type": "paragraph",
        "content": "Increasing font size is an option only when that size fits the content hierarchy. Do not mark small text as large because it is visually prominent. Use the size and weight in the rendered page to choose the requirement."
      },
      {
        "type": "heading",
        "content": "5. Store a small regression record"
      },
      {
        "type": "list",
        "content": "",
        "items": [
          "Element and state: checkout form, email hint, default state.",
          "Inputs: #9CA3AF text on #FFFFFF; record the final replacement after fixing.",
          "Requirement: normal-text AA, 4.5:1.",
          "Evidence: the computed ratio, result URL, tested viewport and a note about opacity or imagery."
        ]
      },
      {
        "type": "heading",
        "content": "6. Add automation with human review"
      },
      {
        "type": "paragraph",
        "content": "An automated scan can catch many straightforward failures after CSS changes. It can also skip uncertain backgrounds or leave cases for manual review. Treat an absence of automated violations as one input, not a certificate. Revisit image backgrounds, canvas content, overlays, focus appearance and color-only meaning yourself."
      }
    ],
    "sources": [
      {
        "text": "W3C: Contrast (Minimum), WCAG 2.2",
        "href": "https://www.w3.org/WAI/WCAG22/Understanding/contrast-minimum.html"
      },
      {
        "text": "W3C: Non-text Contrast",
        "href": "https://www.w3.org/WAI/WCAG22/Understanding/non-text-contrast.html"
      }
    ],
    "revision": "Rebuilt as a practical page-audit workflow; corrected point/pixel confusion and automated-check limitations.",
    "updated": "2026-09-13"
  }
];

export function getBlogPost(slug: string): BlogPost | undefined {
  return blogPosts.find(p => p.slug === slug);
}

export function getRelatedPosts(currentSlug: string, count: number = 3): BlogPost[] {
  const current = getBlogPost(currentSlug);
  return blogPosts.filter(p => p.slug !== currentSlug).sort((a, b) => {
    const score = (p: BlogPost) => p.tags.filter(tag => current?.tags.includes(tag)).length;
    return score(b) - score(a);
  }).slice(0, count);
}
